/**
 * Phase 41 — Mobile-first Quick Booking flow.
 *
 * Renders a single-column form inside a centered dialog (desktop) /
 * near-full-screen sheet-like dialog (mobile). Designed for managers
 * taking booking calls on a phone — six fields, big tap targets, sticky
 * save action.
 *
 * Phase 50 — the Service block is now a small line-items list (template
 * select + editable € price + "×" delete per row, "+ Add service" below).
 * Everything else stays single-row: one customer, one date + time, one
 * staff member, one address, notes + payment type, one VAT/RCT setting
 * for the whole booking. The line_items array maps 1:1 onto the
 * existing `BookingCreate.line_items` shape that the desktop BookingForm
 * has been sending since Phase 8.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, UserPlus, Check, ArrowUpRight, MapPin, AlertTriangle, Plus, X as XIcon } from "lucide-react";
import TemplateMultiSelect from "@/components/TemplateMultiSelect";
import { api, formatApiError, extractFieldErrors } from "@/lib/api";
import { parseMapsUrl } from "@/lib/mapsUrl";
import ConflictWarningDialog from "@/components/ConflictWarningDialog";
import MapPinPicker from "@/components/MapPinPicker";
import MapsLinkHelper from "@/components/MapsLinkHelper";

const PHONE_DEBOUNCE_MS = 300;

const nextRoundHour = (d = new Date()) => {
  const next = new Date(d);
  next.setHours(d.getHours() + (d.getMinutes() > 0 ? 1 : 0), 0, 0, 0);
  if (next <= d) next.setHours(d.getHours() + 1);
  return next;
};

const toDateInputValue = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

const toTimeInputValue = (d) => {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

export default function QuickBookingForm({ open, onOpenChange, onCreated, initialDate = null, calendarBlocks = [] }) {
  // ----- form state -----------------------------------------------------
  const [date, setDate] = useState(() => toDateInputValue(new Date()));
  const [time, setTime] = useState(() => toTimeInputValue(nextRoundHour(new Date())));
  const [templates, setTemplates] = useState([]);
  // Phase 50 — multi-service line items. Each row: { id (UI key only),
  // template_id, unit_price (string for input control) }. We keep
  // duration_minutes on the backend side derived from the template
  // (no duration override in Quick Add — that's a desktop BookingForm
  // concern). First row stays present even if empty.
  const [lines, setLines] = useState([{ id: "l1", template_id: "", unit_price: "" }]);
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentType, setPaymentType] = useState("");
  // Phase F — form-of-payment (optional)
  const [paidMethods, setPaidMethods] = useState([]);
  const [paidMethodCode, setPaidMethodCode] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const notesRef = useRef(null);
  // Phase 50.2 — when the manager pastes a Google Maps share URL into
  // the address field we capture { url, parsedAddress, lat, lng } here.
  // The URL is sent as `location.location_url` so the "Open in Maps"
  // link can later open the *exact* URL the manager picked.
  const [mapsLink, setMapsLink] = useState(null); // { url, address?, lat?, lng? } | null

  // customer / typeahead
  const [phoneQuery, setPhoneQuery] = useState("");
  const [matches, setMatches] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newCustName, setNewCustName] = useState("");
  const [newCustEmail, setNewCustEmail] = useState("");
  // Phase 50.4 — Phone is the PRIMARY identifier across the customer
  // catalogue. The Quick Add inline form was missing it.
  const [newCustPhone, setNewCustPhone] = useState("");
  // Soft duplicate-phone warning. Populated on phone blur / pre-save
  // when the search endpoint returns an existing customer.
  const [dupCustomer, setDupCustomer] = useState(null);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  // Phase 59.3 — "Private cash · one-off" mode. When enabled the customer
  // picker collapses to a phone + optional display-name input. On submit a
  // hidden one-off customer is created (or matched if same phone exists)
  // and the booking is forced to payment_type=simple_cash.
  const [oneOffMode, setOneOffMode] = useState(false);
  const [oneOffPhone, setOneOffPhone] = useState("");
  const [oneOffDisplay, setOneOffDisplay] = useState("");
  const [creatingOneOff, setCreatingOneOff] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  // Phase P.27.1 — shadcn AlertDialog for the "day is blocked" soft warning.
  // `null` = no dialog; object = pending confirmation. We stash the resolved
  // block row so the description can render its `reason` verbatim.
  const [blockConfirm, setBlockConfirm] = useState(null);
  // Phase 47 — per-field error map populated from a 422 response. Used
  // to outline the offending field red and scroll it into view.
  const [fieldErrors, setFieldErrors] = useState({});
  // refs for scroll-into-view on 422.
  const fieldRefs = useRef({});
  // Phase 48 — conflict-warning state.
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const pendingPayloadRef = useRef(null);
  const debounceRef = useRef(null);

  // Phase 42 — travel taxi-meter pill state.
  const [travel, setTravel] = useState(null); // { from_previous, no_data, loading }
  const travelDebounceRef = useRef(null);
  // Phase 42 — map-pin fallback state.
  const [showPinPicker, setShowPinPicker] = useState(false);
  const [pinCoords, setPinCoords] = useState(null);

  // ----- bootstrap -----------------------------------------------------
  useEffect(() => {
    if (!open) return;
    // Reset form on re-open. Phase 45 — when an `initialDate` is passed
    // (e.g. from the DayDetailDrawer "+ Add booking" button) honour it
    // so the booking lands on the user-selected day, not "today".
    const now = new Date();
    const seedDate = initialDate instanceof Date && !isNaN(initialDate.getTime()) ? initialDate : now;
    setDate(toDateInputValue(seedDate));
    setTime(toTimeInputValue(nextRoundHour(seedDate)));
    // Phase 47 — clear any leftover field-error state from a previous
    // failed submit so the form opens clean.
    setFieldErrors({});
    // Phase 50 — single empty line on open.
    setLines([{ id: "l1", template_id: "", unit_price: "" }]);
    setStaffId(""); setPaymentType("");
    setAddress(""); setNotes("");
    setMapsLink(null);
    setPhoneQuery(""); setMatches([]); setSelectedCustomer(null);
    setShowAddNew(false); setNewCustName(""); setNewCustEmail(""); setNewCustPhone(""); setDupCustomer(null);
    setSubmitting(false);
    setTravel(null); setPinCoords(null); setShowPinPicker(false);

    (async () => {
      try {
        const [tpls, st, pts, settings, pmsRes] = await Promise.all([
          api.get("/templates").then((r) => r.data || []),
          api.get("/staff").then((r) => r.data || []),
          api.get("/settings/payment-types").then((r) => r.data || []),
          api.get("/settings").then((r) => r.data || {}),
          api.get("/settings/paid-methods").then((r) => r.data || []).catch(() => []),
        ]);
        setTemplates(tpls.filter((t) => t.active !== false));
        setStaff(st.filter((s) => s.active !== false));
        setPaymentTypes(pts);
        setPaidMethods(Array.isArray(pmsRes) ? pmsRes : []);
        // Default payment_type from settings.default_payment_type_code.
        const defaultPt = settings.default_payment_type_code
          || (pts.find((p) => p.is_default)?.code)
          || pts[0]?.code
          || "";
        setPaymentType(defaultPt);
      } catch (e) {
        toast.error("Failed to load templates / staff");
      }
    })();
  }, [open]);

  // ----- phone typeahead -----------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = phoneQuery.trim();
    if (!q) { setMatches([]); setShowAddNew(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const digits = q.replace(/\D/g, "");
        const url = digits.length >= 2 ? `/customers/search?phone=${encodeURIComponent(q)}`
                                       : `/customers?q=${encodeURIComponent(q)}`;
        const { data } = await api.get(url);
        const limited = (data || []).slice(0, 5);
        setMatches(limited);
        setShowAddNew(limited.length === 0);
      } catch {
        setMatches([]); setShowAddNew(true);
      }
    }, PHONE_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [phoneQuery, open]);

  // ----- Phase 42 travel-meter ----------------------------------------
  // Debounced (500ms) call to POST /api/bookings/travel-preview whenever
  // Customer + Address + Date + Time + Staff are all set. Pill hidden when
  // staff is unassigned. Pin coords override the text address.
  // Phase 50 — duration comes from the *first* line's template (the
  // backend will sum durations across all lines on save; we don't try
  // to mirror that complexity in the live travel pill).
  const firstTemplateId = lines[0]?.template_id || "";
  useEffect(() => {
    if (!open) return;
    if (travelDebounceRef.current) clearTimeout(travelDebounceRef.current);
    if (!selectedCustomer || !staffId || !date || !time || !firstTemplateId) {
      setTravel(null);
      return;
    }
    const addressText = address.trim();
    if (!addressText && !pinCoords) {
      setTravel(null);
      return;
    }
    setTravel({ loading: true });
    travelDebounceRef.current = setTimeout(async () => {
      try {
        const iso = new Date(`${date}T${time}:00`).toISOString();
        const tpl = templates.find((t) => t.id === firstTemplateId);
        const duration_minutes = tpl?.default_duration_minutes || 60;
        const location = pinCoords
          ? { address_line: addressText || "(pin)", lat: pinCoords.lat, lng: pinCoords.lng, postcode: "" }
          : { address_line: addressText, postcode: "" };
        const { data } = await api.post("/bookings/travel-preview", {
          staff_id: staffId,
          scheduled_start: iso,
          duration_minutes,
          location,
        });
        setTravel({
          loading: false,
          from_previous: data?.from_previous || null,
          no_data: !data?.from_previous,
        });
      } catch {
        setTravel(null);
      }
    }, 500);
    return () => travelDebounceRef.current && clearTimeout(travelDebounceRef.current);
  }, [open, selectedCustomer, staffId, date, time, firstTemplateId, address, pinCoords, templates]);

  // ----- pick a customer → load defaults -------------------------------
  const selectCustomer = async (cust) => {
    setSelectedCustomer(cust);
    setMatches([]); setShowAddNew(false);
    setPhoneQuery(cust.phones?.[0] || cust.contact_name || "");
    try {
      const { data } = await api.get(`/customers/${cust.id}/last-booking-defaults`);
      if (data) {
        if (data.payment_type) setPaymentType(data.payment_type);
        if (data.address) setAddress(data.address);
        if (data.default_staff_id) setStaffId(data.default_staff_id);
      }
    } catch { /* defaults are optional */ }
  };

  // ----- inline "Add new customer" --------------------------------------
  // Phase 50.4 — phone is the primary identifier; we now collect it on
  // the inline mini-form, normalise lightly (strip everything that
  // isn't a digit or a leading "+", require ≥7 digits), and surface a
  // soft duplicate warning when an existing customer shares the phone.
  const _normPhone = (raw) => {
    const t = String(raw || "").trim();
    if (!t) return "";
    const lead = t[0] === "+" ? "+" : "";
    return lead + t.replace(/\D/g, "");
  };
  const _phoneValid = (raw) => {
    const norm = _normPhone(raw);
    const digits = norm.replace(/\D/g, "");
    return digits.length >= 7;
  };

  // Look up by phone whenever the field blurs (or when the user clicks
  // Save before we got the chance to). Surfaces a soft warning row with
  // "Use existing" / "Create new anyway" buttons — never blocks the
  // manager, just slows them down enough to catch the dup case.
  const checkPhoneForDuplicate = async () => {
    const norm = _normPhone(newCustPhone);
    if (!_phoneValid(norm)) { setDupCustomer(null); return; }
    try {
      const { data } = await api.get("/customers/search", { params: { phone: norm } });
      const list = Array.isArray(data) ? data : [];
      // Pick the first match — `search` already returns newest-first /
      // exact-phone-first based on the existing backend ranking.
      setDupCustomer(list[0] || null);
    } catch { setDupCustomer(null); }
  };

  const createInlineCustomer = async () => {
    const name = newCustName.trim();
    const phone = _normPhone(newCustPhone);
    // Phase P.4 Item 1 — mirror the Phase P.3 backend relaxation: phone is
    // now the primary identifier. Name (company) becomes optional so a
    // manager on a busy dispatch call can save a customer with just the
    // phone and fill in details later. Backend flags `details_pending=true`.
    if (!_phoneValid(phone)) {
      toast.error("Phone is required (at least 7 digits)");
      return;
    }
    setCreatingCustomer(true);
    try {
      const body = {
        company_name: name,  // May be empty — backend accepts phone-only.
        contact_name: name,
        phones: [phone],
        emails: newCustEmail.trim() ? [{ email: newCustEmail.trim(), receives_invoices: true }] : [],
        notes: "Created via Quick Booking",
      };
      const { data } = await api.post("/customers", body);
      if (data.details_pending) {
        toast.success("Customer saved — details to follow");
      } else {
        toast.success(`Customer added — ${data.company_name || data.contact_name}`);
      }
      await selectCustomer(data);
      setShowAddNew(false);
      setNewCustName(""); setNewCustEmail(""); setNewCustPhone(""); setDupCustomer(null);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not create customer");
    } finally {
      setCreatingCustomer(false);
    }
  };

  // "Use existing" button on the dup warning — slot the matched
  // customer straight into the booking flow and tear down the inline
  // mini-form so the manager keeps moving.
  const useExistingDup = async () => {
    if (!dupCustomer) return;
    await selectCustomer(dupCustomer);
    setShowAddNew(false);
    setNewCustName(""); setNewCustEmail(""); setNewCustPhone(""); setDupCustomer(null);
  };

  // ----- submit ---------------------------------------------------------
  // Phase 46 — `staffId` is now required for canSave. The backend
  // `BookingCreate` schema requires `staff_id: str`; sending `null`
  // returned a Pydantic 422 (`[{type, loc, msg, input, url}]`) which
  // crashed React when the old toast tried to render the array as JSX.
  // Phase 50 — at least one line must have a template selected. We
  // silently drop any blank rows on submit (manager may have tapped "+
  // Add service" and changed their mind).
  const validLines = lines.filter((l) => !!l.template_id);
  const totalPrice = validLines.reduce((sum, l) => {
    const p = Number(l.unit_price);
    return sum + (Number.isFinite(p) ? p : 0);
  }, 0);
  const canSave = (oneOffMode ? !!oneOffPhone.trim() : !!selectedCustomer)
    && validLines.length >= 1 && !!staffId && !!date && !!time && !!paymentType && !submitting;

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const onLineTemplateChange = (idx, newTemplateId) => {
    const tpl = templates.find((t) => t.id === newTemplateId);
    // Prefill the unit_price with the template's default whenever the
    // manager picks a different template (or first picks one). They can
    // still edit afterwards.
    setLines((prev) => prev.map((l, i) => (
      i === idx
        ? { ...l, template_id: newTemplateId, unit_price: tpl ? String(Number(tpl.default_price ?? 0).toFixed(2)) : "" }
        : l
    )));
  };
  const addLine = () => {
    setLines((prev) => [...prev, { id: `l${Date.now()}`, template_id: "", unit_price: "" }]);
  };
  const removeLine = (idx) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  // Phase P.13.1 — Bulk template multi-select. Adapts a list of selected
  // template ids into the underlying `lines` array. Keeps existing lines
  // whose template is still in the selection (so custom unit_price edits
  // aren't clobbered), removes lines dropped from selection, and appends
  // new lines for newly-checked templates with the template default
  // price prefilled.
  const selectedTemplateIds = lines
    .filter((l) => !!l.template_id)
    .map((l) => l.template_id);

  const onMultiTemplateChange = (nextIds) => {
    setLines((prev) => {
      const kept = prev.filter((l) => !l.template_id || nextIds.includes(l.template_id));
      const keptIds = new Set(kept.map((l) => l.template_id).filter(Boolean));
      const appended = nextIds
        .filter((tid) => !keptIds.has(tid))
        .map((tid) => {
          const tpl = templates.find((t) => t.id === tid);
          return {
            id: `l${Date.now()}${Math.random().toString(36).slice(2, 5)}`,
            template_id: tid,
            unit_price: tpl ? String(Number(tpl.default_price ?? 0).toFixed(2)) : "",
          };
        });
      const next = [...kept, ...appended];
      // Ensure at least one row exists so the placeholder UI still renders.
      if (next.length === 0) {
        return [{ id: "l1", template_id: "", unit_price: "" }];
      }
      return next;
    });
  };

  const submit = async () => {
    if (!canSave) {
      // Surface what's missing so the user knows why the button is
      // disabled when it grabs focus from the keyboard.
      const missing = [];
      if (oneOffMode) {
        if (!oneOffPhone.trim()) missing.push("phone number");
      } else if (!selectedCustomer) {
        missing.push("customer");
      }
      if (validLines.length < 1) missing.push("at least one service");
      if (!staffId) missing.push("staff");
      if (!date || !time) missing.push("date/time");
      if (!paymentType) missing.push("payment type");
      if (missing.length) toast.error(`Please pick: ${missing.join(", ")}`);
      return;
    }
    // Phase P.27.1 — soft-warn if the target day is blocked via a shadcn
    // AlertDialog (replaces the native `window.confirm` from P.27). We
    // pause here and let the dialog resume submission via `_doSubmit()`.
    const block = (calendarBlocks || []).find((b) => b?.date === date);
    if (block) {
      setBlockConfirm(block);
      return;
    }
    await _doSubmit();
  };

  const _doSubmit = async () => {
    // Real submit body — unchanged from Phase 41/59 apart from having the
    // block-day gate extracted above. Kept in a second function so both
    // the "not blocked" (direct) and "blocked → Book anyway" (dialog)
    // paths share exactly the same request logic.
    setBlockConfirm(null);
    setSubmitting(true);
    try {
      // Phase 59.3 — when one-off mode is on, create the hidden customer
      // first; the backend is idempotent on matching phones so calling it
      // twice with the same number won't generate duplicates. We then
      // force the payment_type to simple_cash regardless of the picker.
      let resolvedCustomerId;
      let forcedPaymentType = paymentType;
      if (oneOffMode) {
        setCreatingOneOff(true);
        try {
          const r = await api.post("/customers/one-off", {
            phone: oneOffPhone.trim(),
            display_name: oneOffDisplay.trim() || undefined,
          });
          resolvedCustomerId = r.data.id;
          forcedPaymentType = "simple_cash";
        } finally {
          setCreatingOneOff(false);
        }
      } else {
        resolvedCustomerId = selectedCustomer.id;
      }
      const iso = new Date(`${date}T${time}:00`).toISOString();
      const payload = {
        customer_id: resolvedCustomerId,
        staff_id: staffId,
        scheduled_start: iso,
        // Phase 50 — full multi-line payload. `quantity` always 1 in
        // Quick Add; the desktop form handles richer cases. Server
        // accepts a manager-supplied `unit_price` per line and uses it
        // verbatim (overriding the template default).
        line_items: validLines.map((l) => {
          const priceNum = Number(l.unit_price);
          const item = { template_id: l.template_id, quantity: 1 };
          if (Number.isFinite(priceNum) && l.unit_price !== "") item.unit_price = priceNum;
          return item;
        }),
        location: (() => {
          // Phase 50.2 — build the location object once with sane fallbacks.
          //   - manager-supplied Maps URL → carry it through
          //   - pin coords (from MapPinPicker OR parsed from Maps URL) → use them
          //   - address text → use it as address_line, never standalone if both empty
          const base = mapsLink?.url ? { location_url: mapsLink.url } : {};
          if (pinCoords) {
            return {
              ...base,
              address_line: address.trim() || "(pin location)",
              postcode: "",
              lat: pinCoords.lat,
              lng: pinCoords.lng,
            };
          }
          if (address.trim()) {
            return { ...base, address_line: address.trim(), postcode: "" };
          }
          // Maps URL with no parseable address/coords still gets saved
          // so the "Open in Maps" link works later.
          return mapsLink?.url ? { ...base, address_line: "(Maps link)", postcode: "" } : undefined;
        })(),
        notes: notes.trim(),
        payment_type: forcedPaymentType,
        discount_type: "none",
        discount_value: 0,
      };
      // Phase 48 — soft conflict check before commit. Warn modal lets
      // the manager override.
      try {
        // Phase 50 — sum durations across all lines for the conflict
        // check + booking total duration. Server does the same.
        const dur = validLines.reduce((sum, l) => {
          const tpl = templates.find((t) => t.id === l.template_id);
          return sum + Number(tpl?.default_duration_minutes || 60);
        }, 0) || 60;
        const { data: check } = await api.post("/bookings/check-conflicts", {
          staff_id: staffId,
          customer_id: selectedCustomer.id,
          scheduled_start: iso,
          duration_minutes: dur,
          location: payload.location || null,
        });
        if (check?.settings_enabled && (check.conflicts || []).length > 0) {
          pendingPayloadRef.current = payload;
          setConflicts(check.conflicts);
          setConflictOpen(true);
          setSubmitting(false);
          return;
        }
      } catch { /* non-fatal — fall through to actual create */ }
      await commitBooking(payload);
    } catch (e) {
      // Phase 46 — never render error objects directly. `formatApiError`
      // accepts the whole axios error and returns a string.
      // Phase 47 — also split the 422 into per-field errors so the
      // offending input(s) outline red + scroll into view.
      const { fieldErrors: fe, generalMessage } = extractFieldErrors(e);
      setFieldErrors(fe);
      // Scroll the first field-error into view (smooth) so the user
      // doesn't have to hunt for it.
      const firstField = Object.keys(fe)[0];
      if (firstField && fieldRefs.current[firstField]) {
        try {
          fieldRefs.current[firstField].scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* ignore */ }
      }
      toast.error(generalMessage || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  // Phase 48 — extracted commit so the conflict modal can re-fire it on
  // "Save anyway". Always closes the form on success.
  const commitBooking = async (payload) => {
    const { data } = await api.post("/bookings", payload, {
      params: { notify_staff: false },
    });
    toast.success(`Booking created — ${data.booking_code}`);
    onCreated?.(data);
    onOpenChange(false);
  };
  const onConflictProceed = async () => {
    setConflictOpen(false);
    const payload = pendingPayloadRef.current;
    pendingPayloadRef.current = null;
    if (!payload) return;
    setSubmitting(true);
    try {
      await commitBooking(payload);
    } catch (e) {
      const { fieldErrors: fe, generalMessage } = extractFieldErrors(e);
      setFieldErrors(fe);
      toast.error(generalMessage || "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md w-full max-h-[92vh] overflow-y-auto p-0 gap-0"
        data-testid="quick-booking-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-lg">Quick Booking</DialogTitle>
          <DialogDescription className="text-xs">
            Six fields. Save lands a confirmed booking on the calendar.
          </DialogDescription>
        </DialogHeader>

        {/* SCROLLABLE BODY */}
        <div className="px-5 py-4 space-y-5">
          {/* Customer */}
          <div className="space-y-2" data-testid="qb-customer-section">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">Customer</Label>
              <button
                type="button"
                className={`text-[11px] mono uppercase tracking-wider px-2 py-1 rounded-md border transition-colors ${
                  oneOffMode
                    ? "bg-violet-100 text-violet-700 border-violet-300"
                    : "bg-secondary text-muted-foreground border-border hover:border-violet-300 hover:text-violet-700"
                }`}
                onClick={() => {
                  setOneOffMode((v) => !v);
                  // When switching modes, clear the other side so we don't
                  // submit stale customer state.
                  if (!oneOffMode) {
                    setSelectedCustomer(null);
                    setPhoneQuery("");
                  } else {
                    setOneOffPhone("");
                    setOneOffDisplay("");
                  }
                }}
                data-testid="qb-oneoff-toggle"
                title={oneOffMode ? "Switch back to regular customer search" : "Private cash · no invoice"}
              >
                {oneOffMode ? "✓ One-off" : "Private cash · one-off"}
              </button>
            </div>
            {oneOffMode ? (
              <div className="space-y-2 border border-violet-300 bg-violet-50/40 rounded-md p-3"
                   data-testid="qb-oneoff-panel">
                <div className="text-[11px] text-violet-700 leading-relaxed">
                  Recording a private cash job. We&apos;ll create a hidden one-off
                  customer just for this booking. Payment is forced to
                  <span className="font-bold"> Simple Cash</span>.
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Phone</Label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    placeholder="+353 87 …"
                    className="h-12 text-base"
                    value={oneOffPhone}
                    onChange={(e) => setOneOffPhone(e.target.value)}
                    autoFocus
                    data-testid="qb-oneoff-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">
                    Display name <span className="text-[10px] text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    type="text"
                    placeholder="e.g. Mike — Coolock pump call"
                    className="h-11 text-base"
                    value={oneOffDisplay}
                    onChange={(e) => setOneOffDisplay(e.target.value)}
                    data-testid="qb-oneoff-display"
                  />
                </div>
              </div>
            ) : selectedCustomer ? (
              <div className="flex items-center justify-between border rounded-md px-3 py-3 bg-secondary/40"
                   data-testid="qb-selected-customer">
                <div className="text-sm min-w-0">
                  <Link
                    to={`/customers/${selectedCustomer.id}`}
                    className="font-semibold hover:underline hover:text-accent transition-colors truncate block"
                    title={`Open ${selectedCustomer.company_name || selectedCustomer.contact_name} profile`}
                    data-testid="qb-selected-customer-link"
                  >
                    {selectedCustomer.company_name || selectedCustomer.contact_name}
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {selectedCustomer.phones?.[0] || ""}
                    {selectedCustomer.contact_name && selectedCustomer.company_name
                      ? ` · ${selectedCustomer.contact_name}` : ""}
                  </div>
                </div>
                <Button variant="ghost" size="sm" className="h-8 text-xs"
                        onClick={() => { setSelectedCustomer(null); setPhoneQuery(""); }}
                        data-testid="qb-clear-customer">
                  Change
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <Input
                    type="text"
                    inputMode="search"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="Search by phone or name"
                    className="pl-9 h-12 text-base"
                    value={phoneQuery}
                    onChange={(e) => setPhoneQuery(e.target.value)}
                    autoFocus
                    data-testid="qb-customer-search"
                  />
                </div>
                {matches.length > 0 && (
                  <ul className="border rounded-md divide-y" data-testid="qb-customer-matches">
                    {matches.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-3 hover:bg-secondary/50 transition-colors flex items-center justify-between"
                          onClick={() => selectCustomer(c)}
                          data-testid={`qb-customer-match-${c.id}`}
                        >
                          <div>
                            <div className="text-sm font-semibold">{c.company_name || c.contact_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {c.phones?.[0] || ""}
                              {c.contact_name && c.company_name ? ` · ${c.contact_name}` : ""}
                            </div>
                          </div>
                          <Check className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {showAddNew && !creatingCustomer && (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 gap-2"
                    onClick={() => {
                      // Seed the phone field with whatever digits the
                      // manager already typed into the search — they
                      // just confirmed there's no match so the search
                      // text is most likely the new customer's phone.
                      const digits = phoneQuery.replace(/[^\d+]/g, "");
                      setNewCustPhone(digits);
                      setNewCustName("");
                      setShowAddNew(true);
                      setDupCustomer(null);
                      // Kick off a dup check on whatever we just seeded.
                      setTimeout(() => { checkPhoneForDuplicate(); }, 0);
                    }}
                    data-testid="qb-add-new-customer-btn"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add new customer
                  </Button>
                )}
                {showAddNew && (
                  <div className="border rounded-md p-3 space-y-2 bg-secondary/30"
                       data-testid="qb-add-new-customer-form">
                    {/* Phase 50.4 — Phone is the primary identifier per
                        the original PRD. Sits ABOVE name so the manager
                        types it first; we run a soft duplicate-phone
                        check on blur and surface a warning row that
                        offers "Use existing" / "Create new anyway". */}
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wider">Phone</Label>
                      <Input
                        type="tel"
                        inputMode="tel"
                        value={newCustPhone}
                        onChange={(e) => {
                          setNewCustPhone(e.target.value);
                          // Clear the previous dup row as soon as they
                          // change the phone — we'll re-check on blur.
                          if (dupCustomer) setDupCustomer(null);
                        }}
                        onBlur={checkPhoneForDuplicate}
                        placeholder="+353 87 123 4567"
                        className="h-11"
                        data-testid="qb-new-customer-phone"
                      />
                    </div>
                    {dupCustomer && (
                      <div
                        className="border border-amber-300 bg-amber-50 rounded-md p-2 text-[12px] text-amber-900 space-y-2"
                        data-testid="qb-new-customer-dup-warning"
                      >
                        <div className="flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" />
                          <div>
                            A customer with this phone already exists:{" "}
                            <span className="font-bold">
                              {dupCustomer.company_name || dupCustomer.contact_name}
                            </span>.
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-[11px] border-amber-400 bg-white"
                            onClick={useExistingDup}
                            data-testid="qb-new-customer-use-existing"
                          >
                            <Check className="h-3 w-3 mr-1" /> Use existing
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 text-[11px] text-amber-900 hover:bg-amber-100"
                            onClick={() => setDupCustomer(null)}
                            data-testid="qb-new-customer-create-anyway"
                          >
                            Create new anyway
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wider">
                        Customer name <span className="text-muted-foreground normal-case font-normal">(optional — fill in later)</span>
                      </Label>
                      <Input
                        value={newCustName}
                        onChange={(e) => setNewCustName(e.target.value)}
                        placeholder="e.g. RBC Civils Ltd (leave blank if unknown)"
                        className="h-11"
                        data-testid="qb-new-customer-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[11px] uppercase tracking-wider">Email (optional)</Label>
                      <Input
                        type="email"
                        value={newCustEmail}
                        onChange={(e) => setNewCustEmail(e.target.value)}
                        placeholder="ops@rbcivils.example"
                        className="h-11"
                        data-testid="qb-new-customer-email"
                      />
                    </div>
                    {/* Phase P.4 Item 1 — helper note when the manager is
                        about to save with phone-only. `details_pending` will
                        be flagged server-side so the customer shows the red
                        "Details to follow" badge everywhere. */}
                    {(!newCustName.trim() || !newCustEmail.trim()) && _phoneValid(newCustPhone) && (
                      <div
                        className="text-[11px] text-red-700 border border-red-200 bg-red-50 rounded px-2 py-1"
                        data-testid="quick-add-customer-details-pending-note"
                      >
                        Customer will be saved with {newCustName.trim() ? "phone + name" : "phone only"} — you can add {[
                          !newCustName.trim() && "company name",
                          !newCustEmail.trim() && "email",
                        ].filter(Boolean).join(" + ")} later.
                      </div>
                    )}
                    <Button
                      type="button"
                      onClick={createInlineCustomer}
                      disabled={creatingCustomer || !_phoneValid(newCustPhone)}
                      className="w-full h-11"
                      data-testid="qb-new-customer-submit"
                    >
                      {creatingCustomer && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                      {creatingCustomer ? "Adding…" : "Add customer & continue"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Date + time */}
          <div className="grid grid-cols-2 gap-3" data-testid="qb-datetime-section">
            <div
              className="space-y-1"
              ref={(el) => { if (el) fieldRefs.current.scheduled_start = el; }}
            >
              <Label className="text-xs uppercase tracking-wider font-semibold">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (fieldErrors.scheduled_start) setFieldErrors((p) => { const { scheduled_start: _, ...r } = p; return r; });
                }}
                className={`h-12 text-base ${fieldErrors.scheduled_start ? "border-red-500 ring-1 ring-red-500" : ""}`}
                data-testid="qb-date-input"
              />
              {fieldErrors.scheduled_start && (
                <div className="text-[11px] text-red-700 mt-1" data-testid="qb-field-error-scheduled_start">
                  {fieldErrors.scheduled_start}
                </div>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider font-semibold">Time</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => {
                  setTime(e.target.value);
                  if (fieldErrors.scheduled_start) setFieldErrors((p) => { const { scheduled_start: _, ...r } = p; return r; });
                }}
                className={`h-12 text-base ${fieldErrors.scheduled_start ? "border-red-500 ring-1 ring-red-500" : ""}`}
                step="900"
                data-testid="qb-time-input"
              />
            </div>
          </div>

          {/* Phase 50 — Service line items.
              Manager can stack multiple services with per-line price
              overrides. First row is always present; "+ Add service"
              adds more, the "×" removes a row (disabled when it's the
              only one). Live total updates the Save button label too. */}
          <div
            className="space-y-2"
            data-testid="qb-services-section"
            ref={(el) => { if (el) fieldRefs.current.line_items = el; }}
          >
            <Label className="text-xs uppercase tracking-wider font-semibold flex items-center justify-between">
              <span>Services</span>
              <span className="mono text-[11px] text-muted-foreground normal-case tracking-normal" data-testid="qb-services-total">
                Total · €{totalPrice.toFixed(2)}
              </span>
            </Label>
            {/* Phase P.19 — Removed redundant top multi-select. The
                per-line pickers below (with "+ Add service") ARE the
                multi-service picker. Underlying state contract unchanged. */}
            <div className="space-y-2" data-testid="qb-template-list">
              {lines.map((line, idx) => {
                const tpl = templates.find((t) => t.id === line.template_id);
                return (
                  <div
                    key={line.id}
                    className="flex items-stretch gap-2"
                    data-testid={`qb-line-${idx}`}
                  >
                    <Select
                      value={line.template_id || "__none__"}
                      onValueChange={(v) => onLineTemplateChange(idx, v === "__none__" ? "" : v)}
                    >
                      <SelectTrigger
                        className="flex-1 min-w-0 h-11"
                        data-testid={`qb-line-${idx}-template-trigger`}
                      >
                        <SelectValue placeholder="Pick a service">
                          {tpl ? (
                            <div className="truncate text-left">
                              <span className="font-semibold">{tpl.name}</span>
                              <span className="text-[11px] text-muted-foreground ml-2">
                                {tpl.default_duration_minutes || 0}m
                              </span>
                            </div>
                          ) : "Pick a service"}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" data-testid={`qb-line-${idx}-template-none`}>
                          — Pick a service —
                        </SelectItem>
                        {templates.map((t) => (
                          <SelectItem
                            key={t.id}
                            value={t.id}
                            data-testid={`qb-line-${idx}-template-${t.id}`}
                          >
                            <span className="font-semibold">{t.name}</span>
                            <span className="text-[11px] text-muted-foreground ml-2">
                              €{Number(t.default_price || 0).toFixed(2)} · {t.default_duration_minutes || 0}m
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="relative shrink-0 w-[110px]">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">€</span>
                      <Input
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        value={line.unit_price}
                        onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                        placeholder="0.00"
                        className="h-11 pl-6 text-right mono"
                        aria-label={`Price for line ${idx + 1}`}
                        data-testid={`qb-line-${idx}-price`}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeLine(idx)}
                      disabled={lines.length <= 1}
                      className="shrink-0 h-11 w-11"
                      aria-label={`Remove line ${idx + 1}`}
                      data-testid={`qb-line-${idx}-remove`}
                      title={lines.length <= 1 ? "At least one service is required" : "Remove line"}
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
              {templates.length === 0 && (
                <div className="text-xs text-muted-foreground px-1 py-2">
                  No active templates. Create one in Settings → Templates.
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addLine}
              disabled={templates.length === 0}
              className="w-full h-9 mt-1"
              data-testid="qb-add-line"
            >
              <Plus className="h-4 w-4 mr-1" /> Add service
            </Button>
          </div>

          {/* Staff */}
          <div
            className="space-y-2"
            data-testid="qb-staff-section"
            ref={(el) => { if (el) fieldRefs.current.staff_id = el; }}
          >
            <Label className="text-xs uppercase tracking-wider font-semibold">Staff</Label>
            <Select
              value={staffId || "__none__"}
              onValueChange={(v) => {
                setStaffId(v === "__none__" ? "" : v);
                if (fieldErrors.staff_id) setFieldErrors((p) => { const { staff_id: _, ...r } = p; return r; });
              }}
            >
              <SelectTrigger
                className={`h-12 text-base ${fieldErrors.staff_id ? "border-red-500 ring-1 ring-red-500" : ""}`}
                data-testid="qb-staff-trigger"
              >
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="qb-staff-unassigned">Unassigned</SelectItem>
                {staff.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`qb-staff-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldErrors.staff_id && (
              <div className="text-[11px] text-red-700 mt-1" data-testid="qb-field-error-staff_id">
                {fieldErrors.staff_id}
              </div>
            )}
          </div>

          {/* Address */}
          <div className="space-y-2" data-testid="qb-address-section">
            <Label className="text-xs uppercase tracking-wider font-semibold">Site address</Label>
            <div className="flex gap-2">
              <Input
                value={address}
                onChange={(e) => {
                  const v = e.target.value;
                  // Phase 50.2 — detect a pasted Google Maps share URL
                  // and extract its address + coords. We do this on
                  // every change so paste-edit-paste flows behave; if
                  // the value is no longer a Maps URL we clear the
                  // parsed link state so the booking saves with plain
                  // text, not a stale URL.
                  const parsed = parseMapsUrl(v);
                  if (parsed) {
                    setMapsLink(parsed);
                    // Show the human-readable address when we got one,
                    // otherwise leave the URL itself in the input so
                    // the manager can see what they pasted.
                    if (parsed.address) {
                      setAddress(parsed.address);
                    } else {
                      setAddress(v);
                    }
                    // If the URL carried coords, use them for travel /
                    // conflict checks. Wipe any previous map-pin coords
                    // since the URL is the source of truth now.
                    if (Number.isFinite(parsed.lat) && Number.isFinite(parsed.lng)) {
                      setPinCoords({ lat: parsed.lat, lng: parsed.lng });
                    } else {
                      setPinCoords(null);
                    }
                  } else {
                    setMapsLink(null);
                    setAddress(v);
                    setPinCoords(null);
                  }
                }}
                placeholder={selectedCustomer ? "Defaulted from last booking" : "Address or paste Google Maps link"}
                className="h-12 text-base flex-1"
                data-testid="qb-address-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPinPicker(true)}
                className="h-12 px-3"
                title="Drop a pin on the map"
                data-testid="qb-open-map-pin"
              >
                <MapPin className="h-4 w-4" />
              </Button>
            </div>
            {mapsLink && (
              <div
                className="text-[11px] text-muted-foreground flex items-center gap-1 mono"
                data-testid="qb-maps-link-pill"
              >
                <ArrowUpRight className="h-3 w-3 text-accent" />
                Maps link saved
                {Number.isFinite(mapsLink.lat) && Number.isFinite(mapsLink.lng)
                  ? <> · {mapsLink.lat.toFixed(4)}, {mapsLink.lng.toFixed(4)}</>
                  : null}
              </div>
            )}
            {pinCoords && !mapsLink && (
              <div className="text-[11px] text-muted-foreground mono" data-testid="qb-pin-coords">
                Pin set: {pinCoords.lat.toFixed(5)}, {pinCoords.lng.toFixed(5)}
              </div>
            )}
            {/* Phase 42 — travel-time taxi-meter pill */}
            {travel?.loading && (
              <div className="text-xs text-muted-foreground flex items-center gap-2"
                   data-testid="qb-travel-pill-loading">
                <Loader2 className="h-3 w-3 animate-spin" /> Calculating travel…
              </div>
            )}
            {travel && !travel.loading && travel.no_data && staffId && (
              <div className="text-xs text-muted-foreground italic"
                   data-testid="qb-travel-pill-empty">
                First booking of the day for the assigned staff.
              </div>
            )}
            {travel && !travel.loading && travel.from_previous && (
              <div
                className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs leading-relaxed border ${
                  travel.from_previous.warning_flag
                    ? "border-red-300 bg-red-50 text-red-800"
                    : "border-accent/30 bg-accent/5 text-foreground"
                }`}
                data-testid="qb-travel-pill"
                title={travel.from_previous.warning_flag
                  ? "Tight buffer — staff may be late"
                  : "Travel projection from the previous job that day"}
              >
                {travel.from_previous.warning_flag
                  ? <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  : <ArrowUpRight className="h-4 w-4 shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div>
                    From previous job (<span className="font-semibold">{travel.from_previous.customer_name}</span>
                    {travel.from_previous.ends_at ? `, ends ${travel.from_previous.ends_at}` : ""}):
                    {" "}
                    {travel.from_previous.travel_minutes != null
                      ? <span className="font-bold">{Math.round(travel.from_previous.travel_minutes)} min</span>
                      : <span className="italic">travel unknown</span>}
                    {travel.from_previous.distance_km != null
                      ? ` · ${travel.from_previous.distance_km} km`
                      : ""}
                    {travel.from_previous.arrival_time
                      ? ` · Staff arrives ${travel.from_previous.arrival_time}`
                      : ""}
                  </div>
                  {travel.from_previous.warning_flag && (
                    <div className="text-[11px] mt-1">
                      Tight buffer — consider shifting this booking later.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2" data-testid="qb-notes-section">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">Notes (optional)</Label>
              <MapsLinkHelper
                textareaRef={notesRef}
                value={notes}
                onChange={setNotes}
              />
            </div>
            <Textarea
              ref={notesRef}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="text-base"
              placeholder="Site contact, access notes…"
              data-testid="qb-notes-input"
            />
          </div>

          {/* Payment type */}
          <div className="space-y-2" data-testid="qb-payment-section">
            <Label className="text-xs uppercase tracking-wider font-semibold">Payment type</Label>
            <Select
              value={paymentType}
              onValueChange={setPaymentType}
              onOpenChange={async (open) => {
                // Phase E — refetch on open so newly created Payment Types
                // appear immediately (no page reload required).
                if (!open) return;
                try {
                  const { data } = await api.get("/settings/payment-types");
                  if (Array.isArray(data)) setPaymentTypes(data);
                } catch { /* silent */ }
              }}
            >
              <SelectTrigger className="h-12 text-base" data-testid="qb-payment-trigger">
                <SelectValue placeholder="Choose payment type" />
              </SelectTrigger>
              <SelectContent>
                {paymentTypes.map((p) => (
                  <SelectItem key={p.code} value={p.code} data-testid={`qb-payment-${p.code}`}>
                    {p.name}{p.off_the_books ? " · off-books" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Phase F — optional Form of payment select. */}
            <div className="mt-3 space-y-2">
              <Label className="text-xs uppercase tracking-wider font-semibold">Form of payment (optional)</Label>
              <Select
                value={paidMethodCode || "__unset__"}
                onValueChange={(v) => setPaidMethodCode(v === "__unset__" ? "" : v)}
                onOpenChange={async (open) => {
                  if (!open) return;
                  try {
                    const { data } = await api.get("/settings/paid-methods");
                    if (Array.isArray(data)) setPaidMethods(data);
                  } catch { /* silent */ }
                }}
              >
                <SelectTrigger className="h-12 text-base" data-testid="qb-paid-method-select">
                  <SelectValue placeholder="— not set yet —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unset__" data-testid="qb-paid-method-unset">— not set yet —</SelectItem>
                  {paidMethods.map((pm) => (
                    <SelectItem key={pm.id} value={pm.code} data-testid={`qb-paid-method-${pm.code}`}>
                      {pm.name}{pm.user_name ? ` · → ${pm.user_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* STICKY SAVE BAR */}
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t px-5 py-3 z-10"
             data-testid="qb-save-bar">
          <Button
            type="button"
            onClick={submit}
            disabled={!canSave}
            className="w-full h-12 text-base font-bold"
            data-testid="qb-save-button"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {submitting
              ? "Saving…"
              : totalPrice > 0
                ? `Save · €${totalPrice.toFixed(2)}`
                : "Save booking"}
          </Button>
        </div>
      </DialogContent>
      <MapPinPicker
        open={showPinPicker}
        onOpenChange={setShowPinPicker}
        initialQuery={address}
        customerId={selectedCustomer?.id || null}
        onPicked={(p) => {
          setPinCoords({ lat: p.lat, lng: p.lng });
          if (p.formatted_address) setAddress(p.formatted_address);
        }}
      />
      <ConflictWarningDialog
        open={conflictOpen}
        onOpenChange={setConflictOpen}
        conflicts={conflicts}
        onProceed={onConflictProceed}
        onCancel={() => { setConflictOpen(false); pendingPayloadRef.current = null; }}
      />
    </Dialog>
    {/* Phase P.27.1 — shadcn AlertDialog replacing the P.27 native
        `window.confirm` for the "day is blocked" soft warning. */}
    <ConfirmDialog
      state={blockConfirm && {
        title: "This day is blocked",
        description: <><span className="font-semibold">Reason:</span> {blockConfirm.reason || "no reason given"}</>,
        subtitle: "Are you sure you want to create a booking on this day?",
        confirmLabel: "Book anyway",
        destructive: "amber",
        testId: "blocked-day-confirm-dialog",
        onConfirm: () => _doSubmit(),
      }}
      onOpenChange={() => setBlockConfirm(null)}
    />
    </>

  );
}
